import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Theme from './Theme';

import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import LandingPageLoggedOut from 'pages/LandingPageLoggedOut/LandingPageLoggedOut';

import UnavailableProjectPage from 'pages/UnavailableProjectPage/UnavailableProjectPage';
import ErrorScreen from 'components/ErrorScreen/ErrorScreen';

import WorkflowRunsPage from 'pages/WorkflowRunsPage/WorkflowRunsPage';
import WorkflowRunDetailsPage from 'pages/WorkflowRunDetailsPage/WorkflowRunDetailsPage';
import StartWorkflowRunPage from 'pages/StartWorkflowRunPage/StartWorkflowRunPage';
import NewFromRedirect from 'pages/StartWorkflowRunPage/NewFromRedirect';
import DashboardPage from 'pages/DashboardPage/DashboardPage';
import NodesPage from 'pages/NodesPage/NodesPage';
import ProfileSettingsPage from 'pages/ProfileSettingsPage/ProfileSettingsPage';

import ZipProgresses from 'shared/ZipProgresses/ZipProgresses';
import EnvironmentRibbon from 'components/EnvironmentRibbon/EnvironmentRibbon';

import useFetchProjects from 'store/useFetchProjects';
import useFetchProfile from 'store/useFetchProfile';
import useProjectsAsync from 'store/useProjectsAsync';
import useTrackAuthState from 'store/useTrackAuthState';
import useAuthStateAsync from 'store/useAuthStateAsync';

export default function App() {
  return (
    <Theme>
      <BrowserRouter>
        <RouterContent />
      </BrowserRouter>

      <ZipProgresses />
      <EnvironmentRibbon />
    </Theme>
  );
}

function RouterContent() {
  useTrackAuthState();
  const authStateAsync = useAuthStateAsync();

  switch (authStateAsync.status) {
    case 'INIT':
    case 'PENDING':
      return (
        <>
          <LoadingScreen>Authenticating</LoadingScreen>
        </>
      );
    case 'ERROR':
      return (
        <>
          <LandingPageLoggedOut />
          <Navigate to="/" replace />
        </>
      );
    case 'SUCCESS':
    case 'REFRESH':
      if (!authStateAsync.data.authenticated) {
        return (
          <>
            <LandingPageLoggedOut />
            <Navigate to="/" replace />
          </>
        );
      } else {
        return <LoggedInRoutes />;
      }
  }
}

function LoggedInRoutes() {
  useFetchProjects();
  useFetchProfile();

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/nodes" element={<NodesPage />} />
      <Route path="/settings/profile" element={<ProfileSettingsPage />} />
      <Route path="*" element={<ProjectRoutes />} />
    </Routes>
  );
}

function ProjectRoutes() {
  const projectsAsync = useProjectsAsync();

  switch (projectsAsync.status) {
    case 'INIT':
    case 'PENDING':
      return <LoadingScreen />;
    case 'ERROR':
      return <ErrorScreen />;
    case 'SUCCESS':
    case 'REFRESH': {
      const projects = projectsAsync.data;

      return (
        <Routes>
          {projects.map((project) => (
            <Route key={project.id} path={`/projects/${project.id}/*`} element={<RoutesForProject />} />
          ))}
          <Route path="/projects/:projectId/*" element={<UnavailableProjectPage />} />
        </Routes>
      );
    }
  }
}

function RoutesForProject() {
  return (
    <Routes>
      <Route path="" element={<Navigate to="workflows" replace />} />
      <Route path="workflows" element={<WorkflowRunsPage />} />
      <Route path="workflows/:runId/details" element={<WorkflowRunDetailsPage />} />
      <Route path="workflows/new" element={<StartWorkflowRunPage />} />
      <Route path="workflows/new-from/:runId" element={<NewFromRedirect />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
